<template>
    <component
        :is="tagName"
        :href="hrefFormatted"
        class="button"
        :class="classes"
        :data-test="dataTest"
        :data-testid="dataTestid"
        v-bind="attributes"
        ref="rootElement"
        @click="buttonClickHandler"
    >
        <span v-if="iconLeft" class="button__icon">
            <template v-if="size === 'sm'">
                <DsIcon :name="getThinIconName(iconLeft)" :is-in-storybook :width="16" :height="16" />
            </template>
            <template v-else-if="size === 'lg'">
                <DsIcon :name="getRegularIconName(iconLeft)" :is-in-storybook :width="24" :height="24" />
            </template>
            <template v-else>
                <DsIcon :name="iconLeft" :is-in-storybook />
            </template>
        </span>
        <span v-if="!!slots.default" class="button__inner">
            <slot></slot>
        </span>
        <span v-if="iconRight" class="button__icon">
            <template v-if="size === 'sm'">
                <DsIcon :name="getThinIconName(iconRight)" :is-in-storybook :width="16" :height="16" />
            </template>
            <template v-else-if="size === 'lg'">
                <DsIcon :name="getRegularIconName(iconRight)" :is-in-storybook :width="24" :height="24" />
            </template>
            <template v-else>
                <DsIcon :name="iconRight" :is-in-storybook />
            </template>
        </span>
    </component>
</template>

<script lang="ts" setup>
import { computed, ref, useSlots } from "vue";
import { MobileAppMethodName } from "@sazka/mobile-apps/mobile-app-models";
import { DsButtonExpose, DsButtonRefType } from "../button/ds-button-reference";
import DsIcon from "../icon/ds-icon.vue";
import { SvgIcons } from "../icon/types";
import { ButtonType } from "../types/button";
import { Size } from "../types/size";
import { Theme } from "../types/theme";

const slots = useSlots();

const rootElement = ref<DsButtonRefType>(null);

defineExpose<DsButtonExpose>({
    rootElement,
});

const props = withDefaults(
    defineProps<{
        tag?: "a" | "button" | "auto";
        href?: string;
        type?: ButtonType;
        theme?: Theme;
        size?: Size;
        isDisabled?: boolean;
        iconLeft?: SvgIcons;
        iconRight?: SvgIcons;
        isInStorybook?: boolean;
        openInNewTab?: boolean;
        isExternal?: boolean;
        isBlock?: boolean;
        dataTest?: string;
        dataTestid?: string;
        htmlType?: "button" | "submit";
        eventName?: MobileAppMethodName | Omit<string, MobileAppMethodName>;
        eventArgument?: string;
    }>(),
    {
        tag: "auto",
        type: "primary",
        theme: "light",
        size: "default",
        isInStorybook: false,
        openInNewTab: false,
        isExternal: false,
        isBlock: false,
        htmlType: "button",
    }
);

const mobileAppIntegration: Record<"isMobileApp" | "triggerMobileAppEvent", any> = {
    isMobileApp: null,
    triggerMobileAppEvent: null,
};

if (!props.isInStorybook) {
    import("@sazka/mobile-apps/mobile-app-integration").then(({ isMobileApp, triggerMobileAppEvent }) => {
        mobileAppIntegration["isMobileApp"] = isMobileApp;
        mobileAppIntegration["triggerMobileAppEvent"] = triggerMobileAppEvent;
    });
}

const hrefFormatted = computed(() => props.href?.replace("~", ""));

const tagName = computed(() => {
    if (props.tag === "auto") {
        return hrefFormatted.value ? "a" : "button";
    }
    return props.tag;
});

const classes = computed(() => [
    `button--${props.type}`,
    props.theme === "dark" ? "button--dark" : "",
    props.size !== "default" ? `button--${props.size}` : "",
    props.isDisabled ? "button--disabled" : "",
    props.iconLeft || props.iconRight ? "button--icon" : "",
    props.isBlock ? "button--block" : "",
]);

const attributes = computed(() => {
    const attributes = {
        target: null,
        rel: null,
        disabled: props.isDisabled ? "" : undefined,
        type: null,
    };

    if (tagName.value == "button") {
        attributes.type = props.htmlType;
        return attributes;
    }

    if (props.openInNewTab || props.isExternal) {
        attributes.target = "_blank";
        attributes.rel = "noopener";
    }
    if (props.isExternal) {
        attributes.rel += " noreferrer";
    }

    return attributes;
});

const regexp = /.*-thin/gm;

const getThinIconName = (iconName: SvgIcons): SvgIcons => {
    return regexp.test(iconName) ? iconName : (`${iconName}-thin` as SvgIcons);
};

const getRegularIconName = (iconName: SvgIcons): SvgIcons => {
    return !regexp.test(iconName) ? iconName : (iconName.slice(0, -5) as SvgIcons);
};

const buttonClickHandler = (event: Event) => {
    if (props.isInStorybook) return;

    if (props.isDisabled) {
        event.preventDefault();
    } else if (props.eventName && mobileAppIntegration.isMobileApp) {
        event.preventDefault();
        mobileAppIntegration.triggerMobileAppEvent(props.eventName as string, props.eventArgument);
    }
};
</script>
